import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type TouchEvent as ReactTouchEvent } from 'react';
import {
  SIDEBAR_DESKTOP_BREAKPOINT,
  SIDEBAR_WIDTH_MOBILE,
  SIDEBAR_WIDTH_MOBILE_SM,
} from '@config/layout';

const EDGE_ZONE_PX = 24;
const HORIZONTAL_LOCK_PX = 12;
const MAX_VERTICAL_DRIFT_PX = 48;
const OPEN_CLOSE_THRESHOLD_RATIO = 0.28;
const BACKDROP_MAX_OPACITY = 0.6;

type GestureMode = 'open' | 'close';

interface GestureState {
  startX: number;
  startY: number;
  mode: GestureMode;
  locked: boolean;
}

function isMobileViewport(): boolean {
  return typeof window !== 'undefined' && window.innerWidth < SIDEBAR_DESKTOP_BREAKPOINT;
}

function getMobileSidebarWidth(): number {
  if (typeof window === 'undefined') return SIDEBAR_WIDTH_MOBILE;
  return window.innerWidth >= 640 ? SIDEBAR_WIDTH_MOBILE : SIDEBAR_WIDTH_MOBILE_SM;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export interface SidebarMobileGesturesResult {
  isDragging: boolean;
  showBackdrop: boolean;
  sidebarStyle: CSSProperties | undefined;
  backdropStyle: CSSProperties | undefined;
  panelTouchHandlers: {
    onTouchStart: (event: ReactTouchEvent) => void;
    onTouchMove: (event: ReactTouchEvent) => void;
    onTouchEnd: (event: ReactTouchEvent) => void;
    onTouchCancel: () => void;
  };
}

export function useSidebarMobileGestures(
  isOpen: boolean,
  openSidebar: () => void,
  closeSidebar: () => void
): SidebarMobileGesturesResult {
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const gestureRef = useRef<GestureState | null>(null);
  const sidebarWidthRef = useRef(getMobileSidebarWidth());

  const resetGesture = useCallback(() => {
    gestureRef.current = null;
    setIsDragging(false);
    setDragOffset(0);
  }, []);

  const refreshSidebarWidth = useCallback(() => {
    sidebarWidthRef.current = getMobileSidebarWidth();
  }, []);

  const applyHorizontalLock = useCallback((dx: number, dy: number): boolean => {
    const gesture = gestureRef.current;
    if (!gesture) return false;
    if (gesture.locked) return true;

    if (Math.abs(dy) > MAX_VERTICAL_DRIFT_PX && Math.abs(dy) > Math.abs(dx)) {
      resetGesture();
      return false;
    }

    if (Math.abs(dx) >= HORIZONTAL_LOCK_PX && Math.abs(dx) > Math.abs(dy)) {
      gesture.locked = true;
      setIsDragging(true);
    }

    return gesture.locked;
  }, [resetGesture]);

  const handleDocumentTouchStart = useCallback(
    (event: TouchEvent) => {
      if (!isMobileViewport() || isOpen) return;

      const touch = event.touches[0];
      if (touch.clientX > EDGE_ZONE_PX) return;

      refreshSidebarWidth();
      gestureRef.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        mode: 'open',
        locked: false,
      };
    },
    [isOpen, refreshSidebarWidth]
  );

  const handleDocumentTouchMove = useCallback(
    (event: TouchEvent) => {
      const gesture = gestureRef.current;
      if (!gesture || gesture.mode !== 'open') return;

      const touch = event.touches[0];
      const dx = touch.clientX - gesture.startX;
      const dy = touch.clientY - gesture.startY;

      if (!applyHorizontalLock(dx, dy)) return;

      const width = sidebarWidthRef.current;
      setDragOffset(clamp(dx - width, -width, 0));

      if (event.cancelable) {
        event.preventDefault();
      }
    },
    [applyHorizontalLock]
  );

  const finishOpenGesture = useCallback(
    (event: TouchEvent) => {
      const gesture = gestureRef.current;
      if (!gesture || gesture.mode !== 'open') return;

      if (!gesture.locked) {
        resetGesture();
        return;
      }

      const touch = event.changedTouches[0];
      const dx = touch.clientX - gesture.startX;
      const width = sidebarWidthRef.current;
      const threshold = width * OPEN_CLOSE_THRESHOLD_RATIO;

      if (dx > threshold) {
        openSidebar();
      }

      resetGesture();
    },
    [openSidebar, resetGesture]
  );

  useEffect(() => {
    if (isOpen) return;

    document.addEventListener('touchstart', handleDocumentTouchStart, { passive: true });
    document.addEventListener('touchmove', handleDocumentTouchMove, { passive: false });
    document.addEventListener('touchend', finishOpenGesture, { passive: true });
    document.addEventListener('touchcancel', resetGesture, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleDocumentTouchStart);
      document.removeEventListener('touchmove', handleDocumentTouchMove);
      document.removeEventListener('touchend', finishOpenGesture);
      document.removeEventListener('touchcancel', resetGesture);
    };
  }, [
    isOpen,
    handleDocumentTouchStart,
    handleDocumentTouchMove,
    finishOpenGesture,
    resetGesture,
  ]);

  useEffect(() => {
    if (!isDragging && isOpen) {
      setDragOffset(0);
    }
  }, [isOpen, isDragging]);

  useEffect(() => {
    const onResize = () => refreshSidebarWidth();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [refreshSidebarWidth]);

  const panelTouchHandlers = useMemo(
    () => ({
      onTouchStart: (event: ReactTouchEvent) => {
        if (!isMobileViewport() || !isOpen) return;

        refreshSidebarWidth();
        const touch = event.touches[0];
        gestureRef.current = {
          startX: touch.clientX,
          startY: touch.clientY,
          mode: 'close',
          locked: false,
        };
      },
      onTouchMove: (event: ReactTouchEvent) => {
        const gesture = gestureRef.current;
        if (!gesture || gesture.mode !== 'close' || !isOpen) return;

        const touch = event.touches[0];
        const dx = touch.clientX - gesture.startX;
        const dy = touch.clientY - gesture.startY;

        if (!applyHorizontalLock(dx, dy)) return;

        const width = sidebarWidthRef.current;
        setDragOffset(clamp(dx, -width, 0));

        if (event.cancelable) {
          event.preventDefault();
        }
      },
      onTouchEnd: (event: ReactTouchEvent) => {
        const gesture = gestureRef.current;
        if (!gesture || gesture.mode !== 'close') return;

        if (!gesture.locked) {
          resetGesture();
          return;
        }

        const touch = event.changedTouches[0];
        const dx = touch.clientX - gesture.startX;
        const width = sidebarWidthRef.current;
        const threshold = width * OPEN_CLOSE_THRESHOLD_RATIO;

        if (dx < -threshold) {
          closeSidebar();
        }

        resetGesture();
      },
      onTouchCancel: resetGesture,
    }),
    [isOpen, closeSidebar, applyHorizontalLock, refreshSidebarWidth, resetGesture]
  );

  const sidebarStyle = useMemo((): CSSProperties | undefined => {
    if (!isMobileViewport() || !isDragging) return undefined;
    return { transform: `translateX(${dragOffset}px)` };
  }, [dragOffset, isDragging]);

  const backdropStyle = useMemo((): CSSProperties | undefined => {
    if (!isMobileViewport() || !isDragging) return undefined;

    const width = sidebarWidthRef.current;
    const progress = clamp((width + dragOffset) / width, 0, 1);
    return { opacity: progress * BACKDROP_MAX_OPACITY };
  }, [dragOffset, isDragging]);

  const showBackdrop = isOpen || (isDragging && dragOffset > -sidebarWidthRef.current);

  return {
    isDragging,
    showBackdrop,
    sidebarStyle,
    backdropStyle,
    panelTouchHandlers,
  };
}
